import { parse } from "@xmpp/jid"
/**
 * A JID is an XMPP identifier, `{localpart}@{companyNameKey}`. The localpart
 * is the user's opaque durable handle (FAME-443) — NOT the user id;
 * resolve it via `POST /handles/resolve` to reach the directory.
 */
export type Jid = `${string}@${string}` & { [jidBrand]: true }
declare const jidBrand: unique symbol

/**
 * Helpers for working with bare JIDs so the rest of the app reads/writes them
 * through one place instead of hand-rolling `split("@")` / template strings.
 */

const JID_REGEX = /^[^@]+@[^@]+$/

export const isJid = (value: string): value is Jid => JID_REGEX.test(value)

/**
 * Coerce a string to a validated `Jid`, throwing if it isn't one.
 *
 * Bares by default: Costanza only deals in bare JIDs, but incoming XMPP stanzas
 * can arrive full (e.g. `"32809@acme/web-ab12"`), so the resource is stripped at
 * the ingestion boundary. Pass `bare = false` to validate the value untouched.
 */
export const toJid = (value: string, bare = true): Jid => {
  const jid = bare ? (value.split("/")[0] ?? value) : value
  if (isJid(jid)) return jid

  throw new Error(`Invalid JID: ${value}`)
}

/** Build a bare JID from a user's handle and the tenant vhost (`companyNameKey`). */
export const buildUserJid = (
  handle: number | string,
  companyNameKey: string
): Jid => toJid(`${handle}@${companyNameKey}`)

/** The local part of a bare JID — the user's handle, as a string. */
export const jidLocalPart = (jid: Jid): string => jid.split("@")[0] ?? jid

/** The domain of a bare JID — the tenant vhost, or `conference.<tenant>` for rooms. */
export const jidDomain = (jid: Jid): string => jid.split("@")[1] ?? ""

/**
 * Split a JID into its parts. Accepts bare and full — a bare JID has an empty
 * resource, which is the case our own JIDs are.
 */
export const jidParts = (jid: Jid) => {
  const { local, domain, resource } = parse(jid)
  return { local, domain, resource }
}

/**
 * Whether a bare JID addresses a group — rooms live on the tenant's MUC
 * service (`g-<uuid>@conference.<tenant>`, FAME-243), so the domain prefix is
 * the discriminator between the two conversation shapes.
 */
export const isRoomJid = (jid: Jid): boolean =>
  jidDomain(jid).startsWith("conference.")

/** Build a room's bare JID from a group id and the tenant vhost. */
export const buildRoomJid = (groupId: string, tenant: string): Jid =>
  toJid(`${groupId}@conference.${tenant}`)

/** The tenant vhost a room's members live on (`conference.acme` → `acme`). */
export const roomTenant = (roomJid: Jid): string =>
  jidDomain(roomJid).replace(/^conference\./, "")
